import { useCallback, useState, useEffect } from 'react';
import { useDropzone, DropzoneOptions, FileRejection } from 'react-dropzone';
import { FileHeader } from './FileHeader.tsx';
import { UploadError } from './UploadError.tsx';
import { APP_CONFIG } from '../../config/app.ts';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUpload } from '@fortawesome/free-solid-svg-icons';
import React from 'react';

interface FileObject {
  id: number;
  name: string;
}

interface SingleFileUploadButtonProps {
  onFileUpload: (file: FileObject | null) => void;
  initialFile?: FileObject | null;
  type: string;
  acceptTypes:  {
    'application/pdf': ['.pdf'],
    'application/msword': ['.doc', '.docx'],
    // Agrega más tipos de archivo según tus necesidades
  };
  maxSize: 5;
}

export function SingleFileUploadButton({ onFileUpload, initialFile = null, type, acceptTypes, maxSize }: SingleFileUploadButtonProps) {

 


  const [uploadedFile, setUploadedFile] = useState<FileObject | null>(initialFile);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setUploadedFile(initialFile);
  }, [initialFile]);

  const onDrop = useCallback((acceptedFiles: File[], rejectedFiles: FileRejection[]) => {
    if (rejectedFiles.length > 0) {
      const maxSizeInMB = 5; // Tamaño máximo en MB
      const acceptedFileTypes = ['.pdf', '.doc', '.docx']; // Tipos de archivo permitidos
      
      const errorMessages = rejectedFiles.map((file) => {
        const { errors } = file;
        return errors.map((error) => {
          switch (error.code) {
            case 'file-invalid-type':
              return `El tipo de archivo no es válido. Los tipos de archivo permitidos son: ${acceptedFileTypes.join(', ')}.`;
            case 'file-too-large':
              return `El tamaño del archivo debe ser menor a ${maxSizeInMB} MB.`;
            case 'too-many-files':
              return 'Solo se permite subir un archivo a la vez.';
            default:
              return `${error.message}`;
          }
        });
      });

      console.log(errorMessages);
      setError(errorMessages.join(' '));
      return;
    }


    if (acceptedFiles.length === 0) return;
    console.log(acceptedFiles);
    const file = acceptedFiles[0];
    setIsUploading(true);
    setError(null);

    uploadFile(file, setUploadProgress, type)
      .then((response) => {
        const fileObject: FileObject = {
          id: response.id,
          name: file.name,
        };
        setUploadedFile(fileObject);
        onFileUpload(fileObject);
      })
      .catch((error) => {
        setError('Error al subir el archivo. Por favor, inténtalo de nuevo.');
        console.error('Error al subir el archivo', error);
        setUploadedFile(null);
      })
      .finally(() => {
        setIsUploading(false);
        setUploadProgress(0);
      });
  }, [onFileUpload]);

  const handleDelete = () => {
    setUploadedFile(null);
    setError(null);
    setUploadProgress(0);
    onFileUpload(null);
  };

  const dropzoneOptions: DropzoneOptions = {
    onDrop,
    accept: acceptTypes,
    maxSize: maxSize * 1024 * 1024, // 5MB
    maxFiles: 1,
    disabled: isUploading,
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone(dropzoneOptions);


  return (
    <div>
      <div {...getRootProps()} className={`dropzone btn btn-secondary ${isUploading ? 'disabled' : ''}`}>
        <input {...getInputProps()} />
        {isDragActive ? (
          <span>Suelta el archivo aquí...</span>
        ) : (
          <>
           
            <span>Seleccionar archivo</span>
          </>

        )}
      </div>
      {uploadedFile && !error && (
        <div>
          <FileHeader file={uploadedFile} onDelete={handleDelete} />
          {isUploading && (
            <div className="progress">
              <div
                className="progress-bar"
                role="progressbar"
                style={{ width: `${uploadProgress}%` }}
                aria-valuenow={uploadProgress}
                aria-valuemin={0}
                aria-valuemax={100}
              >
                {uploadProgress}%
              </div>
            </div>
          )}
        </div>
      )}
      {error && <UploadError message={error} onDismiss={() => setError(null)} />}
    </div>
  );
}





function uploadFile(file: File, onProgress: (percentage: number) => void, type: string) {
  const url = `${APP_CONFIG.apiUrl}/upload-file/${type}`;
  console.log(url);
  return new Promise<{ id: number }>((res, rej) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', url);

    xhr.onload = () => {
      if (xhr.status === 200) {
        const resp = JSON.parse(xhr.responseText);
        if (resp.error) {
          rej(new Error(resp.message));
        } else {
          res({ id: resp.id });
        }
      } else {
        rej(new Error('Error en la carga del archivo'));
      }
    };
    xhr.onerror = (evt) => rej(evt);
    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable) {
        const percentage = (event.loaded / event.total) * 100;
        onProgress(Math.round(percentage));
      }
    };

    const formData = new FormData();
    formData.append('file', file);
    xhr.send(formData);
  });
}